import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { useChapterReview } from '../hooks/useChapterReview';
import { useChapterAsk } from '../hooks/useChapterAsk';
import { cn } from '@/utils/cn';
import ReadingChat from '@/features/reading/components/ReadingChat';
import ReadingSettingsMenu from '@/features/reading/components/ReadingSettingsMenu';
import MemoizedCardStack from '@/features/reading/components/MemoizedCardStack';
import DifficultyBadge from '@/features/reading/components/DifficultyBadge';
import { CapacitorUtils } from '@/utils/capacitor';

type CardType = 'summary' | 'question';

interface UnifiedCard {
  type: CardType;
  id: string;
  content?: string;
  questionData?: {
    id: string;
    question: string;
    options: string[];
    correctAnswer: number;
    difficulty?: 'easy' | 'medium' | 'hard' | string;
  };
}

interface ReviewState {
  chapterId: number;
  bookId?: number;
}

const initialChapterSplashShown = new Set<string>();

const SPLASH_DOT_DELAYS = [0.35, 0.65, 0.95] as const;
const SPLASH_DOT_DURATION = 0.2;
const SPLASH_READING_DELAY_MS = 350;
const SPLASH_READING_MS = 1150 + SPLASH_READING_DELAY_MS;

const ReadingSplashOverlay = ({ text, onComplete }: { text: string; onComplete: () => void }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const timer = setTimeout(() => onCompleteRef.current(), SPLASH_READING_MS);
    return () => clearTimeout(timer);
  }, []);

  const sizeClass = text.length > 42
    ? 'text-xl sm:text-2xl md:text-3xl'
    : text.length > 26
      ? 'text-2xl sm:text-3xl'
      : 'text-3xl sm:text-4xl';

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      className="absolute inset-0 z-[100] flex items-center justify-center backdrop-blur-xl bg-black/60 px-4 py-10"
      style={{ touchAction: 'none' }}
    >
      <div className={cn('text-white font-bold font-persian drop-shadow-2xl text-center leading-normal break-words max-w-[92vw]', sizeClass)} dir="rtl">
        <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          {text}
          <span className="inline-flex mr-1 font-sans text-4xl align-middle translate-y-[-4px]">
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SPLASH_DOT_DELAYS[0], duration: SPLASH_DOT_DURATION }}>.</motion.span>
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SPLASH_DOT_DELAYS[1], duration: SPLASH_DOT_DURATION }}>.</motion.span>
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SPLASH_DOT_DELAYS[2], duration: SPLASH_DOT_DURATION }}>.</motion.span>
          </span>
        </motion.span>
      </div>
    </motion.div>
  );
};

const ChapterReview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as ReviewState | null;
  const chapterId = state?.chapterId || 0;
  const bookIdFromState = state?.bookId;
  const splashEntryKey = `${location.key}:${chapterId}`;

  const { data, loading, error, summaryPages, questions, submit, isNovelReview, novelSections } = useChapterReview(chapterId);
  const { ask: chapterAsk } = useChapterAsk();

  const [chatOpen, setChatOpen] = useState(false);
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [uiVisible, setUiVisible] = useState<boolean>(false);
  const [pageBgColor, setPageBgColor] = useState<'white' | 'sepia' | 'dark'>('white');
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [splashState, setSplashState] = useState<'intro' | 'questions' | 'none'>(() => {
    if (initialChapterSplashShown.has(splashEntryKey)) return 'none';
    return 'intro';
  });
  const chapterSplashContextRef = useRef<string | null>(null);
  const questionsSplashShownRef = useRef(false);
  const [answered, setAnswered] = useState<Record<string, number>>({});
  const [selectedOptions, setSelectedOptions] = useState<Record<string, number>>({});
  const [answers, setAnswers] = useState<{ question_id: string; selected_index: number; time_spent?: number }[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [questionAccumulatedTime, setQuestionAccumulatedTime] = useState<Record<string, number>>({});
  const questionSessionStartRef = useRef<Record<string, number>>({});

  const touchStartTimeRef = useRef<number>(0);
  const touchStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const touchHandledRef = useRef<boolean>(false);

  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);

  useEffect(() => {
    const statusBarColor = pageBgColor === 'dark'
      ? '#111827'
      : pageBgColor === 'sepia'
        ? '#f4ecd8'
        : '#ffffff';
    void CapacitorUtils.setStatusBarColor(statusBarColor);
    void CapacitorUtils.setStatusBarStyle(pageBgColor === 'dark' ? 'light' : 'dark');
  }, [pageBgColor]);

  useEffect(() => () => {
    void CapacitorUtils.setStatusBarColor('#ffffff');
    void CapacitorUtils.setStatusBarStyle('dark');
  }, []);

  useLayoutEffect(() => {
    if (chapterSplashContextRef.current === splashEntryKey) return;
    if (chapterSplashContextRef.current === null) {
      chapterSplashContextRef.current = splashEntryKey;
      initialChapterSplashShown.add(splashEntryKey);
      return;
    }
    setSplashState(initialChapterSplashShown.has(splashEntryKey) ? 'none' : 'intro');
    initialChapterSplashShown.add(splashEntryKey);
    setCurrentCardIndex(0);
    questionsSplashShownRef.current = false;
  }, [chapterId, splashEntryKey]);

  const handleIntroSplashComplete = useCallback(() => {
    setSplashState('none');
  }, []);

  const handleQuestionsSplashComplete = useCallback(() => {
    setSplashState('none');
  }, []);

  useEffect(() => {
    if (chatOpen) setHasOpenedChat(true);
  }, [chatOpen]);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  useEffect(() => {
    if (loading || !data || !isNovelReview || novelSections.length === 0) return;

    const firstSection = novelSections[0];
    const localBookId = Number(localStorage.getItem('lastLearningPathBookId') || 0);
    const resolvedBookId = (bookIdFromState && bookIdFromState > 0) ? bookIdFromState : localBookId;
    const reviewSections = novelSections.map((section) => ({
      sectionId: section.section_id,
      sectionTitle: section.title || 'بخش داستان',
      paragraphs: (section.content?.paragraphs || []).map((p) => ({
        index: p.index,
        content: p.content,
        reactions: p.reactions || [],
      })),
    }));

    navigate('/novel-read', {
      replace: true,
      state: {
        bookId: resolvedBookId,
        chapterId: data.chapter_id,
        sectionId: firstSection.section_id,
        sectionTitle: firstSection.title || 'بخش داستان',
        previous_section_summary: null,
        trailer: null,
        returnTo: '/review/result',
        reviewMode: 'chapter_review_novel',
        reviewSectionIndex: 0,
        reviewSections,
        content: { paragraphs: reviewSections[0]?.paragraphs || [] },
      },
    });
  }, [loading, data, isNovelReview, novelSections, navigate, bookIdFromState]);

  const toPersianDigits = useCallback((num: number): string => {
    return String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  }, []);

  const getCharLimit = useCallback((): number => {
    const cardInnerHeight = windowHeight - 150;
    const fontPx = 16;
    const lineHeight = fontPx * 2.2;
    const linesPerCard = Math.max(1, Math.floor(cardInnerHeight / lineHeight));
    const cardWidth = Math.min(window.innerWidth * 0.85, 384) - 40;
    const avgCharWidth = fontPx * 0.45;
    const charsPerLine = Math.max(20, Math.floor(cardWidth / avgCharWidth));
    return Math.floor(linesPerCard * charsPerLine * 0.94);
  }, [windowHeight]);

  const splitSummaryByFontSize = useCallback((content: string, charLimit: number): string[] => {
    if (!content || !content.trim()) return [];
    const normalizedContent = content.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
    const sentences = normalizedContent.match(/[^.]+(?:\.|$)/g)?.map((sentence) => sentence.trim()).filter(Boolean)
      || [normalizedContent];
    const words = sentences.flatMap((sentence) =>
      sentence.length > charLimit ? sentence.split(/\s+/).filter(Boolean) : [sentence]
    );
    const pages: string[] = [];
    let currentPage = '';

    for (const word of words) {
      const separator = currentPage ? ' ' : '';
      const newLength = currentPage.length + separator.length + word.length;
      if (newLength > charLimit && currentPage.trim()) {
        pages.push(currentPage.trim());
        currentPage = word;
      } else {
        currentPage += separator + word;
      }
    }

    if (currentPage.trim()) pages.push(currentPage.trim());
    return pages.length > 0 ? pages : [content];
  }, []);

  const summaryCardContents = useMemo(() => {
    const charLimit = getCharLimit();
    const allSummaryContent = summaryPages.map(p => p.content).join('\n\n');
    return splitSummaryByFontSize(allSummaryContent, charLimit);
  }, [summaryPages, getCharLimit, splitSummaryByFontSize]);

  const unifiedCards = useMemo<UnifiedCard[]>(() => {
    const cards: UnifiedCard[] = [];

    summaryCardContents.forEach((content, idx) => {
      cards.push({ type: 'summary', id: `summary-${idx + 1}`, content });
    });

    if (!loading) {
      questions.forEach((q) => {
        cards.push({
          type: 'question',
          id: `question-${q.id}`,
          questionData: {
            id: q.id,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            difficulty: q.difficulty,
          }
        });
      });
    }

    return cards;
  }, [summaryCardContents, questions, loading]);

  const totalCards = unifiedCards.length;
  const summaryCardCount = summaryCardContents.length;

  const hasQuestionsLoadedRef = useRef(false);

  useEffect(() => {
    if (!loading && questions.length > 0 && !hasQuestionsLoadedRef.current) {
      hasQuestionsLoadedRef.current = true;
      if (currentCardIndex >= summaryCardCount) {
        setCurrentCardIndex(summaryCardCount);
      }
    }
  }, [loading, questions.length, currentCardIndex, summaryCardCount]);

  useEffect(() => {
    if (currentCardIndex >= totalCards && totalCards > 0) {
      setCurrentCardIndex(totalCards - 1);
    }
  }, [totalCards, currentCardIndex]);

  const currentCard = unifiedCards[currentCardIndex];
  const isQuestionCard = currentCard?.type === 'question';
  const currentQuestion = isQuestionCard ? currentCard.questionData : null;
  const currentSelectedOption = currentQuestion ? selectedOptions[currentQuestion.id] : undefined;

  useEffect(() => {
    if (loading || !currentCard) return;

    const now = Date.now();
    const currentQuestionId = currentCard.type === 'question' ? currentCard.questionData?.id : null;

    if (currentCard.type === 'summary') {
      Object.keys(questionSessionStartRef.current).forEach(qId => {
        const sessionStart = questionSessionStartRef.current[qId];
        if (sessionStart) {
          setQuestionAccumulatedTime(prev => ({ ...prev, [qId]: (prev[qId] || 0) + (now - sessionStart) }));
          delete questionSessionStartRef.current[qId];
        }
      });
    } else if (currentQuestionId) {
      Object.keys(questionSessionStartRef.current).forEach(otherQId => {
        if (otherQId !== currentQuestionId) {
          const sessionStart = questionSessionStartRef.current[otherQId];
          if (sessionStart) {
            setQuestionAccumulatedTime(prev => ({ ...prev, [otherQId]: (prev[otherQId] || 0) + (now - sessionStart) }));
            delete questionSessionStartRef.current[otherQId];
          }
        }
      });

      if (answered[currentQuestionId] === undefined && !questionSessionStartRef.current[currentQuestionId]) {
        questionSessionStartRef.current[currentQuestionId] = now;
      }
    }
  }, [currentCard, loading, answered]);

  const onConfirmAnswer = useCallback(() => {
    if (!currentQuestion || currentSelectedOption === undefined) return;

    const qId = currentQuestion.id;
    const now = Date.now();

    let totalTime = questionAccumulatedTime[qId] || 0;
    if (questionSessionStartRef.current[qId]) {
      totalTime += (now - questionSessionStartRef.current[qId]);
      delete questionSessionStartRef.current[qId];
    }

    const timeSpentSeconds = Math.max(0, Math.floor(totalTime / 1000));

    setAnswered(prev => ({ ...prev, [qId]: currentSelectedOption }));
    setAnswers(prev => [...prev, { question_id: qId, selected_index: currentSelectedOption + 1, time_spent: timeSpentSeconds }]);
  }, [currentQuestion, currentSelectedOption, questionAccumulatedTime]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    
    // اگر سوالی نداریم (chapter با status "reviewed")، مستقیماً برگرد به learning path
    if (questions.length === 0 || answers.length === 0) {
      // دریافت bookId از state یا localStorage
      const localBookId = Number(localStorage.getItem('lastLearningPathBookId') || 0);
      const resolvedBookId = (bookIdFromState && bookIdFromState > 0) ? bookIdFromState : localBookId;
      
      navigate('/learning/path', { 
        replace: true,
        state: resolvedBookId > 0 ? { bookId: resolvedBookId } : undefined
      });
      return;
    }
    
    try {
      setSubmitting(true);
      const res = await submit(answers);
      navigate('/review/result', {
        state: {
          accuracy_percentage: res.accuracy_percentage,
          total_time_spent: res.total_time_spent,
          message: res.message,
          correct_answers: res.correct_answers,
          total_questions: res.total_questions,
        }
      });
    } catch (e) {
      console.error('Submit chapter review failed', e);
      alert('خطا در ارسال مرور فصل');
    } finally {
      setSubmitting(false);
    }
  }, [answers, submit, submitting, navigate, questions.length, bookIdFromState]);

  const customAskFunction = useCallback(async (_chapterId: number, question: string) => {
    return chapterAsk(chapterId, question);
  }, [chapterId, chapterAsk]);

  const currentPageContent = useMemo(() => {
    if (currentCard?.type === 'summary') {
      return currentCard.content || '';
    }
    return '';
  }, [currentCard]);

  const paginate = useCallback((newDirection: number) => {
    const newIndex = currentCardIndex + newDirection;
    if (newIndex >= 0 && newIndex < totalCards) {
      if (newDirection > 0) {
        const cardData = unifiedCards[currentCardIndex];
        if (cardData?.type === 'question' && cardData.questionData) {
          const qId = cardData.questionData.id;
          if (answered[qId] === undefined) return;
        }
      }
      setCurrentCardIndex(newIndex);
      if (
        newDirection > 0 &&
        currentCardIndex < summaryCardCount &&
        newIndex >= summaryCardCount &&
        !questionsSplashShownRef.current
      ) {
        questionsSplashShownRef.current = true;
        setSplashState('questions');
      }
    }
  }, [currentCardIndex, totalCards, unifiedCards, answered, summaryCardCount]);

  const onDragEnd = useCallback((_: unknown, { offset }: PanInfo) => {
    const swipeConfidenceThreshold = 40;
    const swipe = offset.y;
    if (swipe < -swipeConfidenceThreshold) paginate(1);
    else if (swipe > swipeConfidenceThreshold) paginate(-1);
  }, [paginate]);

  const cardBgClasses = {
    white: 'bg-[#6e61ff] text-white',
    sepia: 'bg-[#8B5A2B] text-[#f4ecd8]',
    dark: 'bg-[#3b3a5c] text-gray-100'
  };
  const activeQuestionActionClass = pageBgColor === 'dark'
    ? 'bg-white text-[#1f2937] active:scale-95'
    : pageBgColor === 'sepia'
      ? 'bg-[#8B5A2B] text-white active:scale-95'
      : 'bg-[#6e61ff] text-white active:scale-95';
  const disabledQuestionActionClass = pageBgColor === 'dark'
    ? 'disabled:bg-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed'
    : 'disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed';
  const lastQuestionDisabledActionClass = pageBgColor === 'dark'
    ? 'disabled:bg-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed'
    : 'disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed';

  const renderCards = () => {
    const cards = [];
    for (let i = totalCards - 1; i >= 0; i--) {
      const card = unifiedCards[i];
      const diff = i - currentCardIndex;
      if (Math.abs(diff) > 1) continue;

      const isCurrent = diff === 0;
      const opacity = isCurrent ? 1 : 0.6;
      const scale = isCurrent ? 1 : 0.85;
      const yOffset = diff * 110;
      const zIndex = 50 - Math.abs(diff);
      const filterBlur = isCurrent ? '0px' : '6px';
      const isLastCard = i === totalCards - 1;

      cards.push(
        <motion.div
          key={card.id + i}
          className="absolute w-[88%] sm:w-[85%] max-w-sm sm:max-w-md flex flex-col items-center"
          initial={{ y: yOffset + (diff > 0 ? 50 : -50), opacity: 0, scale: scale * 0.9 }}
          animate={{ y: yOffset, opacity, scale }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          drag={isCurrent ? 'y' : false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.8}
          onDragStart={isCurrent ? () => { setUiVisible(false); } : undefined}
          onDragEnd={isCurrent ? onDragEnd : undefined}
          style={{
            zIndex,
            filter: filterBlur,
            touchAction: isCurrent ? 'none' : 'auto',
            pointerEvents: isCurrent ? 'auto' : 'none',
            margin: '0 auto',
            // Preserve the existing blur/scale visuals while keeping the
            // card on a stable compositor layer during a swipe.
            willChange: 'transform, opacity, filter',
            backfaceVisibility: 'hidden',
          }}
        >
          <div
            className={cn(
              'w-full rounded-2xl shadow-2xl flex flex-col overflow-hidden',
              cardBgClasses[pageBgColor],
              isCurrent && 'current-reading-card'
            )}
            style={{
              maxHeight: card.type === 'summary'
                ? 'calc(100dvh - max(2.25rem, calc(1rem + env(safe-area-inset-top) + env(safe-area-inset-bottom))))'
                : 'calc(100dvh - max(3rem, calc(2rem + env(safe-area-inset-top) + env(safe-area-inset-bottom))))'
            }}
          >
            {card.type === 'summary' ? (
              <div className="w-full h-full flex flex-col px-5 py-4">
                <div className="w-full h-full overflow-hidden">
                  <p className="w-full text-justify font-persian whitespace-pre-wrap break-words drop-shadow-sm font-medium text-[17px] leading-[2.2rem] [text-align-last:right]">
                    {card.content}
                  </p>
                </div>
              </div>
            ) : !card.questionData ? (
              <div className="w-full px-5 py-5">
                <div className="w-full h-40 animate-pulse bg-white/20 rounded-xl" />
              </div>
            ) : (
              <div className="w-full px-3 py-4 flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div dir="rtl" className="mb-2 flex w-full justify-start">
                  <DifficultyBadge difficulty={card.questionData.difficulty} />
                </div>
                <h3 className="text-[15.5px] font-bold font-persian text-justify mb-4 leading-relaxed flex-shrink-0 px-1">
                  {card.questionData.question}
                </h3>
                <div className="flex flex-col mt-3 overflow-hidden" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                  <div className="flex flex-col space-y-2 overflow-y-auto hide-scrollbar scroll-smooth pb-3" style={{ touchAction: 'pan-y', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
                  {card.questionData.options.map((option, optIdx) => {
                    const qId = card.questionData!.id;
                    const hasAnswered = answered[qId] !== undefined;
                    const isCorrect = card.questionData!.correctAnswer === optIdx;
                    const isSelected = selectedOptions[qId] === optIdx;
                    const isAnsweredOption = answered[qId] === optIdx;

                    return (
                      <button
                        key={optIdx}
                        onClick={() => !hasAnswered && setSelectedOptions(p => ({ ...p, [qId]: optIdx }))}
                        disabled={hasAnswered}
                        className={cn(
                          'w-full py-1.5 px-2.5 rounded-xl text-justify transition-all font-persian text-[13.5px] leading-snug break-words',
                          'select-none active:scale-[0.98]',
                          hasAnswered && isCorrect && 'bg-green-500/40 border-2 border-green-400',
                          hasAnswered && isAnsweredOption && !isCorrect && 'bg-red-500/40 border-2 border-red-400',
                          !hasAnswered && isSelected && 'bg-white/30 border-2 border-white',
                          !isSelected && !hasAnswered && 'bg-white/10 border border-white/20',
                          hasAnswered && !isCorrect && !isAnsweredOption && 'opacity-50 bg-white/5'
                        )}
                        style={{ touchAction: 'manipulation' }}
                      >
                        {option}
                      </button>
                    );
                  })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {isCurrent && card.type === 'question' && card.questionData && (
            <div className="w-[85%] mt-4">
              {answered[card.questionData.id] === undefined ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onConfirmAnswer(); }}
                  disabled={selectedOptions[card.questionData.id] === undefined}
                  className={cn(
                    'w-full py-3.5 rounded-xl font-bold font-persian transition-all shadow-lg text-sm',
                    selectedOptions[card.questionData.id] !== undefined
                      ? activeQuestionActionClass
                      : (i === totalCards - 1 ? lastQuestionDisabledActionClass : disabledQuestionActionClass)
                  )}
                >
                  ثبت پاسخ
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); i === totalCards - 1 ? handleSubmit() : paginate(1); }}
                  disabled={submitting && i === totalCards - 1}
                  className={cn(
                    'w-full py-3.5 rounded-xl font-bold font-persian transition-all shadow-lg text-sm',
                    activeQuestionActionClass,
                    disabledQuestionActionClass
                  )}
                >
                  {i === totalCards - 1 ? (submitting ? 'در حال ارسال...' : 'اتمام مرور فصل') : 'ادامه'}
                </button>
              )}
            </div>
          )}

          {isCurrent && isLastCard && card.type === 'summary' && questions.length === 0 && !loading && (
            <div className="w-[85%] mt-4">
              <button
                onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
                disabled={submitting}
                className={cn(
                  'w-full py-3.5 rounded-xl font-bold font-persian transition-all shadow-lg text-sm',
                  activeQuestionActionClass,
                  disabledQuestionActionClass
                )}
              >
                {submitting ? 'در حال ارسال...' : 'اتمام مرور'}
              </button>
            </div>
          )}

          {isCurrent && !isLastCard && (
            <div className="mt-2 opacity-40 flex justify-center animate-slow-bounce pointer-events-none">
              <svg className="w-7 h-7 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          )}
        </motion.div>
      );
    }
    return cards;
  };

  const renderedCards = useMemo(() => renderCards(), [
    totalCards,
    unifiedCards,
    currentCardIndex,
    pageBgColor,
    answered,
    selectedOptions,
    submitting,
    questions.length,
    loading,
    activeQuestionActionClass,
    disabledQuestionActionClass,
    lastQuestionDisabledActionClass,
    onConfirmAnswer,
    handleSubmit,
    paginate,
    onDragEnd,
  ]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartTimeRef.current = Date.now();
    touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    touchHandledRef.current = false;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touchDuration = Date.now() - touchStartTimeRef.current;
    const touchEndPos = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    const deltaX = touchEndPos.x - touchStartPosRef.current.x;
    const deltaY = touchEndPos.y - touchStartPosRef.current.y;
    const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]') || target.closest('input') || target.closest('textarea')) {
      return;
    }

    const TAP_THRESHOLD = 15;
    const TIME_THRESHOLD = 400;

    if (touchDuration < TIME_THRESHOLD && movement < TAP_THRESHOLD) {
      setUiVisible(prev => !prev);
      touchHandledRef.current = true;
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }

    setUiVisible(prev => !prev);
  }, []);

  const bgColorClasses = {
    white: 'bg-white',
    sepia: 'bg-[#f4ecd8]',
    dark: 'bg-gray-900'
  };

  if (!state || !chapterId) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', bgColorClasses[pageBgColor])} dir="rtl">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-muted-foreground font-persian">داده‌های مرور فصل یافت نشد</p>
          <button onClick={goBack} className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian">
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  if (error || (!loading && !data)) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', bgColorClasses[pageBgColor])} dir="rtl">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-muted-foreground font-persian">خطا در بارگذاری مرور فصل</p>
          <p className="text-sm text-red-500 mt-2">{error}</p>
          <button onClick={goBack} className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian">
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  if (!loading && isNovelReview) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', bgColorClasses[pageBgColor])} dir="rtl">
        <p className="text-muted-foreground font-persian">در حال انتقال به مطالعه رمان...</p>
      </div>
    );
  }

  const progressPercent = totalCards > 0 ? ((currentCardIndex + 1) / totalCards) * 100 : 0;

  return (
    <div
      className={cn('h-[100svh] w-full transition-colors duration-300 relative flex flex-col overflow-hidden fixed inset-0 touch-none', bgColorClasses[pageBgColor])}
      style={{ overscrollBehavior: 'none', height: '100dvh' }}
      dir="rtl"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {loading && (
        <div className="absolute inset-0 z-[100] backdrop-blur-xl bg-black/60" aria-hidden="true" />
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes slowBounce {
          0%, 100% { transform: translateY(-25%); animation-timing-function: cubic-bezier(0.8,0,1,1); }
          50% { transform: translateY(0); animation-timing-function: cubic-bezier(0,0,0.2,1); }
        }
        .animate-slow-bounce { animation: slowBounce 2.5s infinite; }
      `}</style>

      <>
        {data && splashState === 'intro' && (
          <ReadingSplashOverlay
            text="مرور فصل"
            onComplete={handleIntroSplashComplete}
          />
        )}
        {!loading && data && splashState === 'questions' && (
          <ReadingSplashOverlay text="سوالات" onComplete={handleQuestionsSplashComplete} />
        )}
      </>

      <AnimatePresence>
        {!loading && data && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={cn(
              'fixed top-0 left-0 right-0 z-[90]',
              pageBgColor === 'dark' ? 'bg-gray-900' : pageBgColor === 'sepia' ? 'bg-[#f4ecd8]' : 'bg-white'
            )}
            style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
          >
            <div className="flex items-center gap-4 px-5 pb-1" style={{ paddingTop: 'calc(0.25rem + env(safe-area-inset-top))' }}>
              <button
                onClick={(e) => { e.stopPropagation(); goBack(); }}
                className={cn('p-1.5 rounded-full transition-all active:scale-95', pageBgColor === 'dark' ? 'hover:bg-gray-800/50 bg-gray-800 shadow-md' : 'hover:bg-gray-200/50 bg-white shadow-md border border-gray-200')}
              >
                <svg className={cn('w-5 h-5', pageBgColor === 'dark' ? 'text-gray-200' : 'text-gray-700')} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <div className={cn(
                'flex-1 h-[18px] rounded-full overflow-hidden shadow-inner',
                pageBgColor === 'dark' ? 'bg-gray-800' : pageBgColor === 'sepia' ? 'bg-[#d8ccb8]' : 'bg-gray-200'
              )}>
                <motion.div
                  className={cn(
                    'h-full rounded-full',
                    pageBgColor === 'dark' ? 'bg-[#8a80ff]' : pageBgColor === 'sepia' ? 'bg-[#9a6235]' : 'bg-[#6e61ff]'
                  )}
                  initial={{ width: `${progressPercent}%` }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <span className={cn('text-sm font-bold font-persian min-w-[35px] text-center', pageBgColor === 'dark' ? 'text-gray-200' : 'text-gray-700')}>
                {toPersianDigits(currentCardIndex + 1)} <span className="text-xs opacity-60">از</span> {toPersianDigits(totalCards)}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div 
        className="flex-1 relative flex items-center justify-center w-full"
        style={{
          paddingTop: 'calc(3rem + env(safe-area-inset-top))',
          paddingBottom: 'calc(0.25rem + env(safe-area-inset-bottom))'
        }}
      >
        <MemoizedCardStack>{renderedCards}</MemoizedCardStack>
      </div>

      {hasOpenedChat && <ReadingChat
        open={chatOpen && !isQuestionCard}
        onClose={() => setChatOpen(false)}
        contextText={currentPageContent}
        chapterId={chapterId}
        customAskFunction={customAskFunction}
        bgColor={pageBgColor}
      />}

      <AnimatePresence>
        {uiVisible && !isQuestionCard && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-8 left-0 right-0 z-[60] flex justify-center"
          >
            <div className="relative flex items-center gap-4">
              <button
                onClick={(e) => { e.stopPropagation(); setShowSettingsMenu(prev => !prev); }}
                className={cn(
                  'w-14 h-14 rounded-full shadow-2xl transition-all active:scale-90 flex items-center justify-center border',
                  pageBgColor === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-100 text-gray-600'
                )}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setChatOpen(true); }}
                className="w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all active:scale-90 border bg-white border-gray-100 text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3} d="M8 10h8M8 14h5m-9 5l1.2-3.6a8 8 0 1114.6 0L21 19l-4-1.1A8 8 0 014 19z" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSettingsMenu && (
          <ReadingSettingsMenu
            bgColor={pageBgColor}
            onBgColorChange={setPageBgColor}
            onClose={() => setShowSettingsMenu(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChapterReview;
